/**
 * @file CustomToolTip.tsx
 * @copyright 2024 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2024 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */

import { Fade, SxProps, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import React from "react";
import { AppUtility } from "../../utils/AppUtility";

/**
 * An interface definition for custom ToolTipProps
 */
interface CustomToolTipProps {
    children?: React.ReactNode;
    title: string,
    toolTipLengthLimit: number,
    sx?: SxProps
}

/**
 * A function to apply some css on tool tip.
 */
const TooltipContainer = styled(({ className, sx, ...props }: TooltipProps) => (
    <Tooltip sx={{...sx}} {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: '#021E48'
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#021E48',
        maxWidth: 250,
        color: theme.palette.common.white
    },
}));

/**
 * A function component for creating Custom tool tip.
 * @param props The CustomToolTipProps
 * @returns 
 */
export function CustomToolTip(props: CustomToolTipProps) {
    const { children, title, sx, toolTipLengthLimit: toolTipLimit } = props;
    const toolTipLimitSize = AppUtility.isMobileDeviceScreen() ? 8 : toolTipLimit;
    return (
        <>
            {title.length > toolTipLimitSize &&
                <TooltipContainer TransitionComponent={Fade}
                    TransitionProps={{ timeout: 600 }}
                    sx={{...sx}}
                    title={
                        <React.Fragment>
                            <Typography sx={{fontSize:"1.1em"}} color="inherit">{title}</Typography>
                        </React.Fragment>
                    }
                    arrow followCursor>
                    <div>{children}</div>
                </TooltipContainer>}
            {title.length <= toolTipLimitSize && children}
        </>
    );
}